// Procedural vector art. Everything is drawn with canvas paths + gradients so it
// stays crisp at any DPI and needs no image assets. Cohesive "soft blob" style:
// radial-gradient bodies, dark eyes with highlights, gentle smiles.

import { TAU } from "./math.ts";
import type { Stage } from "./evolution.ts";

/** Linearly blend two #rrggbb colors; t=0 → a, t=1 → b. */
function mix(a: string, b: string, t: number): string {
  const pa = [parseInt(a.slice(1, 3), 16), parseInt(a.slice(3, 5), 16), parseInt(a.slice(5, 7), 16)];
  const pb = [parseInt(b.slice(1, 3), 16), parseInt(b.slice(3, 5), 16), parseInt(b.slice(5, 7), 16)];
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

/** A wobbling organic blob outline, centered at the origin. */
function blobPath(ctx: CanvasRenderingContext2D, r: number, t: number, wobble = 0.06) {
  const points = 14;
  ctx.beginPath();
  for (let i = 0; i <= points; i++) {
    const a = (i / points) * TAU;
    const w = 1 + Math.sin(a * 3 + t * 2.2) * wobble + Math.cos(a * 5 - t * 1.5) * wobble * 0.5;
    const rr = r * w;
    const x = Math.cos(a) * rr;
    const y = Math.sin(a) * rr;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function bodyGradient(ctx: CanvasRenderingContext2D, r: number, color: string, shade: string) {
  const g = ctx.createRadialGradient(-r * 0.3, -r * 0.35, r * 0.1, 0, 0, r * 1.1);
  g.addColorStop(0, color);
  g.addColorStop(1, shade);
  return g;
}

/** Two friendly eyes + a smile, looking toward (dx,dy). */
function drawFace(ctx: CanvasRenderingContext2D, r: number, dx: number, dy: number, blink = 1) {
  const eyeR = r * 0.22;
  const sep = r * 0.4;
  const look = r * 0.08;
  const nx = dx || 0;
  const ny = dy || 0;
  const nlen = Math.hypot(nx, ny) || 1;
  const lx = (nx / nlen) * look;
  const ly = (ny / nlen) * look;

  for (const s of [-1, 1]) {
    const ex = s * sep;
    const ey = -r * 0.15;
    // white
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.ellipse(ex, ey, eyeR, eyeR * blink, 0, 0, TAU);
    ctx.fill();
    // pupil
    ctx.fillStyle = "#232323";
    ctx.beginPath();
    ctx.ellipse(ex + lx, ey + ly, eyeR * 0.55, eyeR * 0.55 * blink, 0, 0, TAU);
    ctx.fill();
    // highlight
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.beginPath();
    ctx.arc(ex + lx - eyeR * 0.2, ey + ly - eyeR * 0.2, eyeR * 0.18, 0, TAU);
    ctx.fill();
  }

  // smile
  ctx.strokeStyle = "rgba(40,30,30,0.65)";
  ctx.lineWidth = Math.max(1.5, r * 0.05);
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.arc(0, r * 0.12, r * 0.3, 0.15 * Math.PI, 0.85 * Math.PI);
  ctx.stroke();
}

/**
 * Draw the player creature for a given stage, centered at the origin.
 * `t` drives idle animation; `dx,dy` is facing direction; `squash` in [~0.8,1.2].
 */
export function drawCreature(
  ctx: CanvasRenderingContext2D,
  stage: Stage,
  r: number,
  t: number,
  dx: number,
  dy: number,
  squash = 1,
  overfull = false,
) {
  ctx.save();
  ctx.scale(1 / Math.sqrt(squash), Math.sqrt(squash));

  // soft ground shadow
  ctx.fillStyle = "rgba(0,0,0,0.14)";
  ctx.beginPath();
  ctx.ellipse(0, r * 1.05, r * 0.85, r * 0.28, 0, 0, TAU);
  ctx.fill();

  // stage-specific extra limbs/features drawn BEHIND the body
  drawBehind(ctx, stage.index, r, t);

  // body — muddied toward brown when overfull with filth
  const bodyColor = overfull ? mix(stage.color, "#6b4a2a", 0.5) : stage.color;
  const bodyShade = overfull ? mix(stage.shade, "#3e2a17", 0.5) : stage.shade;
  ctx.fillStyle = bodyGradient(ctx, r, bodyColor, bodyShade);
  blobPath(ctx, r, t, stage.index <= 1 ? 0.1 : 0.05);
  ctx.fill();

  // gold rim — the thangka gilding
  ctx.strokeStyle = "rgba(233,205,114,0.6)";
  ctx.lineWidth = Math.max(1, r * 0.05);
  ctx.stroke();

  // stage-specific features in FRONT (ears, wings-tips, halo, etc.)
  drawFront(ctx, stage, r, t);

  drawFace(ctx, r, dx, dy);
  ctx.restore();
}

function drawBehind(ctx: CanvasRenderingContext2D, idx: number, r: number, t: number) {
  switch (idx) {
    case 3: {
      // Fly wings
      const flap = Math.sin(t * 20) * 0.3 + 0.7;
      ctx.fillStyle = "rgba(220,235,255,0.55)";
      for (const s of [-1, 1]) {
        ctx.save();
        ctx.translate(s * r * 0.5, -r * 0.4);
        ctx.rotate(s * 0.5);
        ctx.scale(1, flap);
        ctx.beginPath();
        ctx.ellipse(0, 0, r * 0.7, r * 0.35, 0, 0, TAU);
        ctx.fill();
        ctx.restore();
      }
      break;
    }
    case 10: {
      // Buddha halo + radiant glow
      const g = ctx.createRadialGradient(0, 0, r * 0.9, 0, 0, r * 2.2);
      g.addColorStop(0, "rgba(255,220,120,0.55)");
      g.addColorStop(1, "rgba(255,220,120,0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, r * 2.2, 0, TAU);
      ctx.fill();
      // rays
      ctx.strokeStyle = "rgba(255,225,140,0.5)";
      ctx.lineWidth = r * 0.04;
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * TAU + t * 0.2;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * r * 1.25, Math.sin(a) * r * 1.25);
        ctx.lineTo(Math.cos(a) * r * 1.7, Math.sin(a) * r * 1.7);
        ctx.stroke();
      }
      break;
    }
  }
}

function ear(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, tilt: number, color: string) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(tilt);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(-r * 0.25, r * 0.2);
  ctx.quadraticCurveTo(0, -r * 0.5, r * 0.25, r * 0.2);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawFront(ctx: CanvasRenderingContext2D, stage: Stage, r: number, t: number) {
  switch (stage.index) {
    case 1: // Amoeba nucleus
      ctx.fillStyle = "rgba(60,130,140,0.45)";
      ctx.beginPath();
      ctx.arc(r * 0.15, r * 0.2, r * 0.28, 0, TAU);
      ctx.fill();
      break;
    case 2: // Worm segments
      ctx.strokeStyle = stage.shade;
      ctx.lineWidth = r * 0.06;
      for (let i = -1; i <= 1; i++) {
        ctx.beginPath();
        ctx.arc(0, 0, r * (0.55 + i * 0.22), -0.2, Math.PI + 0.2);
        ctx.stroke();
      }
      break;
    case 4: // Frog eyes on top
      for (const s of [-1, 1]) {
        ctx.fillStyle = stage.color;
        ctx.beginPath();
        ctx.arc(s * r * 0.45, -r * 0.85, r * 0.28, 0, TAU);
        ctx.fill();
      }
      break;
    case 5: // Cat ears + whiskers
      ear(ctx, -r * 0.5, -r * 0.8, r, -0.2, stage.shade);
      ear(ctx, r * 0.5, -r * 0.8, r, 0.2, stage.shade);
      ctx.strokeStyle = "rgba(80,60,40,0.5)";
      ctx.lineWidth = r * 0.03;
      for (const s of [-1, 1])
        for (let i = -1; i <= 1; i++) {
          ctx.beginPath();
          ctx.moveTo(s * r * 0.25, r * 0.25 + i * r * 0.08);
          ctx.lineTo(s * r * 0.75, r * 0.2 + i * r * 0.14);
          ctx.stroke();
        }
      break;
    case 6: // Dog floppy ears
      for (const s of [-1, 1]) {
        ctx.fillStyle = stage.shade;
        ctx.beginPath();
        ctx.ellipse(s * r * 0.85, -r * 0.2, r * 0.22, r * 0.5, s * 0.3, 0, TAU);
        ctx.fill();
      }
      break;
    case 7: // Monkey round ears + muzzle
      for (const s of [-1, 1]) {
        ctx.fillStyle = stage.shade;
        ctx.beginPath();
        ctx.arc(s * r * 0.9, -r * 0.1, r * 0.28, 0, TAU);
        ctx.fill();
      }
      ctx.fillStyle = "rgba(255,240,220,0.6)";
      ctx.beginPath();
      ctx.ellipse(0, r * 0.25, r * 0.5, r * 0.4, 0, 0, TAU);
      ctx.fill();
      break;
    case 8: // Human tuft of hair
      ctx.fillStyle = "#5a3a24";
      ctx.beginPath();
      ctx.arc(0, -r * 0.75, r * 0.35, Math.PI, TAU);
      ctx.fill();
      break;
    case 9: // Monk topknot + collar glow
      ctx.fillStyle = stage.shade;
      ctx.beginPath();
      ctx.arc(0, -r * 0.9, r * 0.16, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = "rgba(255,230,150,0.5)";
      ctx.lineWidth = r * 0.06;
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.98, 0.2, Math.PI - 0.2);
      ctx.stroke();
      break;
    case 10: // Buddha topknot + serene dot
      ctx.fillStyle = "#f6c948";
      ctx.beginPath();
      ctx.arc(0, -r * 0.95, r * 0.18, 0, TAU);
      ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.beginPath();
      ctx.arc(0, -r * 0.35, r * 0.06, 0, TAU);
      ctx.fill();
      void t;
      break;
  }
}

/** A cute poop swirl. `golden` renders the Buddha's radiant gold poop. */
export function drawPoop(
  ctx: CanvasRenderingContext2D,
  r: number,
  t: number,
  golden: boolean,
  bob = 0,
) {
  ctx.save();
  ctx.translate(0, bob);

  if (golden) {
    const glow = ctx.createRadialGradient(0, 0, r * 0.4, 0, 0, r * 2);
    glow.addColorStop(0, "rgba(255,215,90,0.55)");
    glow.addColorStop(1, "rgba(255,215,90,0)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(0, 0, r * 2, 0, TAU);
    ctx.fill();
  }

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.12)";
  ctx.beginPath();
  ctx.ellipse(0, r * 0.95, r * 0.9, r * 0.25, 0, 0, TAU);
  ctx.fill();

  const light = golden ? "#ffe27a" : "#8a5a3b";
  const dark = golden ? "#d99a20" : "#5e3a22";

  // three stacked coils, smallest on top
  const coils = [
    { y: r * 0.55, rx: r, ry: r * 0.55 },
    { y: -r * 0.05, rx: r * 0.72, ry: r * 0.42 },
    { y: -r * 0.55, rx: r * 0.45, ry: r * 0.3 },
  ];
  for (const c of coils) {
    const g = ctx.createLinearGradient(0, c.y - c.ry, 0, c.y + c.ry);
    g.addColorStop(0, light);
    g.addColorStop(1, dark);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(0, c.y, c.rx, c.ry, 0, 0, TAU);
    ctx.fill();
  }

  // little peak
  ctx.fillStyle = light;
  ctx.beginPath();
  ctx.arc(0, -r * 0.8, r * 0.14, 0, TAU);
  ctx.fill();

  // tiny face (poops are friendly here)
  ctx.fillStyle = "#241a12";
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.arc(s * r * 0.22, -r * 0.05, r * 0.09, 0, TAU);
    ctx.fill();
  }
  ctx.strokeStyle = "#241a12";
  ctx.lineWidth = Math.max(1, r * 0.06);
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.arc(0, r * 0.05, r * 0.16, 0.15 * Math.PI, 0.85 * Math.PI);
  ctx.stroke();

  void t;
  ctx.restore();
}

const BEING_COLORS: Record<string, [string, string]> = {
  fly: ["#8b90a3", "#565b6e"],
  frog: ["#8fd46f", "#5aa03a"],
  dog: ["#d9b389", "#a9835a"],
  cow: ["#f4f0ea", "#c9c2b6"],
  human: ["#f0c3a0", "#c1906a"],
  monk: ["#e79a5c", "#b96f36"],
  demon: ["#c14a2c", "#7a2417"],
};

export type BeingKind = keyof typeof BEING_COLORS;
export const BEING_KINDS = Object.keys(BEING_COLORS) as BeingKind[];

/** Roaming pooper. Tier scales its size + poop value; `hunting` = chasing you. */
export function drawBeing(
  ctx: CanvasRenderingContext2D,
  kind: BeingKind,
  r: number,
  t: number,
  dx: number,
  dy: number,
  hunting = false,
) {
  const [color, shade] = BEING_COLORS[kind];
  ctx.save();

  // Predators mid-hunt gain a wrathful red aura.
  if (hunting) {
    const aura = ctx.createRadialGradient(0, 0, r * 0.7, 0, 0, r * 1.8);
    aura.addColorStop(0, "rgba(224,80,50,0.4)");
    aura.addColorStop(1, "rgba(224,80,50,0)");
    ctx.fillStyle = aura;
    ctx.beginPath();
    ctx.arc(0, 0, r * 1.8, 0, TAU);
    ctx.fill();
  }

  ctx.fillStyle = "rgba(0,0,0,0.12)";
  ctx.beginPath();
  ctx.ellipse(0, r, r * 0.8, r * 0.24, 0, 0, TAU);
  ctx.fill();

  ctx.fillStyle = bodyGradient(ctx, r, color, shade);
  blobPath(ctx, r, t, 0.05);
  ctx.fill();
  // gold rim — the thangka gilding
  ctx.strokeStyle = "rgba(233,205,114,0.5)";
  ctx.lineWidth = Math.max(1, r * 0.05);
  ctx.stroke();

  // distinguishing bits
  if (kind === "demon") {
    // horns
    ctx.fillStyle = shade;
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(s * r * 0.5, -r * 0.55);
      ctx.quadraticCurveTo(s * r * 0.9, -r * 1.15, s * r * 0.5, -r * 1.0);
      ctx.quadraticCurveTo(s * r * 0.45, -r * 0.7, s * r * 0.5, -r * 0.55);
      ctx.fill();
    }
    // angry brows
    ctx.strokeStyle = "#2a0f08";
    ctx.lineWidth = r * 0.09;
    ctx.lineCap = "round";
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(s * r * 0.16, -r * 0.28);
      ctx.lineTo(s * r * 0.5, -r * 0.12);
      ctx.stroke();
    }
  } else if (kind === "cow") {
    ctx.fillStyle = "rgba(60,50,45,0.6)";
    ctx.beginPath();
    ctx.ellipse(-r * 0.3, r * 0.1, r * 0.25, r * 0.18, 0.3, 0, TAU);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(r * 0.35, -r * 0.2, r * 0.18, r * 0.14, -0.2, 0, TAU);
    ctx.fill();
  } else if (kind === "frog") {
    for (const s of [-1, 1]) {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(s * r * 0.4, -r * 0.8, r * 0.24, 0, TAU);
      ctx.fill();
    }
  } else if (kind === "dog") {
    for (const s of [-1, 1]) {
      ctx.fillStyle = shade;
      ctx.beginPath();
      ctx.ellipse(s * r * 0.8, -r * 0.2, r * 0.2, r * 0.42, s * 0.3, 0, TAU);
      ctx.fill();
    }
  }

  drawFace(ctx, r * 0.9, dx, dy);
  ctx.restore();
}

/** The radiant, floating Buddha that drops golden poop. */
export function drawBuddha(
  ctx: CanvasRenderingContext2D,
  r: number,
  t: number,
  dx: number,
  dy: number,
) {
  ctx.save();
  const float = Math.sin(t * 1.5) * r * 0.05;
  ctx.translate(0, float);

  // radiant aura
  const glow = ctx.createRadialGradient(0, 0, r * 0.8, 0, 0, r * 2.4);
  glow.addColorStop(0, "rgba(255,220,120,0.5)");
  glow.addColorStop(1, "rgba(255,220,120,0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(0, 0, r * 2.4, 0, TAU);
  ctx.fill();

  // rotating rays
  ctx.strokeStyle = "rgba(255,225,140,0.5)";
  ctx.lineWidth = r * 0.05;
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * TAU + t * 0.25;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * r * 1.2, Math.sin(a) * r * 1.2);
    ctx.lineTo(Math.cos(a) * r * 1.7, Math.sin(a) * r * 1.7);
    ctx.stroke();
  }

  // body
  ctx.fillStyle = bodyGradient(ctx, r, "#ffd769", "#e0a52b");
  blobPath(ctx, r, t, 0.03);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = r * 0.04;
  ctx.stroke();

  // topknot + urna dot
  ctx.fillStyle = "#f6c948";
  ctx.beginPath();
  ctx.arc(0, -r * 0.95, r * 0.2, 0, TAU);
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.beginPath();
  ctx.arc(0, -r * 0.35, r * 0.07, 0, TAU);
  ctx.fill();

  // serene closed-eye smile
  ctx.strokeStyle = "rgba(90,60,20,0.7)";
  ctx.lineWidth = r * 0.05;
  ctx.lineCap = "round";
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.arc(s * r * 0.4, -r * 0.15, r * 0.18, 0.15 * Math.PI, 0.85 * Math.PI);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.arc(0, r * 0.1, r * 0.32, 0.1 * Math.PI, 0.9 * Math.PI);
  ctx.stroke();

  void dx;
  void dy;
  ctx.restore();
}
